import React, { useEffect, useRef, useState } from 'react';
import { Button, Text, Grid, GridItem, Flex } from '@chakra-ui/react';
import { SongTabFormWrapper } from 'app/components/Song/SongTabFormWrapper';
import { FormProvider } from 'react-hook-form';
import { UnsavedChangesDialogComponent } from 'app/components/Dialogs/UnsavedChangesDialog/UnsavedChangesDialogComponent';
import { countryToOption } from './utils';
import { DeleteSongContributionDialog } from '../DeleteSongContributionDialog';
import { ContributorFields } from './ContributorFields';
import { LocationFields } from './LocationFields';
import { DateFields } from './DateFields';
import { RoleFields } from './RoleFields';
import { RoleSelectionField } from './RoleSelectionField';
import {
  ContributionFormComponentProps,
  PerformerRoleDetailHistory,
} from './types';
import { useContributionForm } from './hooks';

export const ContributionFormComponent: React.FC<ContributionFormComponentProps> = ({
  contribution,
  countries,
  userCountry,
  createOrUpdateContribution,
  createValidation,
  contributionClassesAndRoles,
  handleContributionDelete,
  affectsSplit,
  setNewContributionClass,
  searchProfilesValidation,
  patchValidation,
  onClose,
}) => {
  const roleFieldRef = useRef<HTMLDivElement>(null);
  const scrollContainerRef = useRef<HTMLFormElement>(null);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);

  const [
    performerRoleDetailHistory,
    setPerformerRoleDetailHistory,
  ] = useState<PerformerRoleDetailHistory>(
    contribution?.role ? { [contribution.role]: contribution?.roleDetail } : {}
  );

  const form = useContributionForm({
    contribution,
    createOrUpdateContribution,
    countries,
    createValidation,
    patchValidation,
    contributionClassesAndRoles,
    onSuccessfulSubmit: onClose,
  });

  const {
    control,
    setValue,
    watchContributorSearch,
    contributionClassName,
    formState,
    getOnSubmit,
    getHandleSubmit,
    getValues,
  } = form;

  const { isValid, isSubmitting, isDirty } = formState;

  useEffect(() => {
    const country = countryToOption(countries[userCountry?.countryCode || '']);
    if (!contribution) {
      setValue('date.date', new Date());
      setValue('location.country', country || null);
    }
    if (
      contributionClassName &&
      roleFieldRef.current &&
      scrollContainerRef.current?.scrollTo
    ) {
      scrollContainerRef.current.scrollTo({
        top: roleFieldRef.current.offsetTop,
        behavior: 'smooth',
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    setNewContributionClass(contributionClassName);
  }, [contributionClassName, setNewContributionClass]);

  const [closing, setClosing] = useState(false);

  return (
    <FormProvider {...form}>
      <SongTabFormWrapper
        onSubmit={getHandleSubmit()}
        ref={scrollContainerRef}
        pb={0}
      >
        <Grid
          gap={5}
          w="full"
          gridTemplateColumns="repeat(3, 1fr)"
          gridTemplateRows="repeat(11, max-content)"
        >
          <GridItem gridColumn="2 / 3" pb={1} minW={0}>
            <Text fontSize="lg" fontWeight={500} textAlign="center">
              {contribution ? 'Edit' : 'Add'} credit
            </Text>
          </GridItem>
          <GridItem gridColumn="2 / 3" pb={4} minW={0}>
            <ContributorFields
              maxSearchLength={searchProfilesValidation.search.maxLength || 100}
              isExistingUser={Boolean(contribution?.user?.userGuid)}
              contributor={watchContributorSearch}
              countriesDDL={countries}
            />
          </GridItem>
          <GridItem gridColumn="1 / 4">
            <RoleSelectionField
              classes={contributionClassesAndRoles.classes}
              contributorClassNameDisabled={Boolean(contribution)}
            />
          </GridItem>
          {contributionClassName && (
            <>
              <RoleFields
                performerRoleDetailHistory={performerRoleDetailHistory}
                contributor={watchContributorSearch}
                contributionClassName={contributionClassName}
                contributionClassesAndRolesDDL={contributionClassesAndRoles}
                roleFieldRef={roleFieldRef}
                setPerformerRoleDetailHistory={setPerformerRoleDetailHistory}
              />
              <DateFields control={control} contribution={contribution} />
              <LocationFields countries={countries} />
            </>
          )}
        </Grid>
        <Flex w="full">
          {contribution && (
            <Button
              my={4}
              color="R.80 !important"
              variant="text"
              onClick={() => setShowDeleteDialog(true)}
            >
              Remove credit
            </Button>
          )}
          <Flex flex={1} justify="flex-end" alignSelf="center" pb={4}>
            <Button
              onClick={() => {
                if (isDirty && !isSubmitting) {
                  return setClosing(true);
                }
                return onClose();
              }}
              variant="secondary"
              data-testid="contribution-dialog-cancel-btn"
            >
              Cancel
            </Button>
            <Button
              ml={2}
              type="submit"
              isDisabled={(isDirty && isSubmitting) || !isValid}
              data-testid="contribution-dialog-submit-btn"
            >
              {contribution ? 'Save' : 'Add credit'}
            </Button>
          </Flex>
        </Flex>
        <DeleteSongContributionDialog
          guid={
            showDeleteDialog && contribution?.guid
              ? contribution.guid
              : undefined
          }
          onClose={() => setShowDeleteDialog(false)}
          onConfirm={(guid) => {
            handleContributionDelete(guid);
            onClose();
          }}
          affectsSplit={affectsSplit}
        />
        <UnsavedChangesDialogComponent
          open={isDirty && !isSubmitting && closing}
          onSubmit={() => getOnSubmit(getValues())}
          onClose={() => setClosing(false)}
          disabled={false}
        />
      </SongTabFormWrapper>
    </FormProvider>
  );
};
